import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || '/api';

function buildTree(categories) {
  const map = {};
  const roots = [];
  categories.forEach(c => { map[c.id] = { ...c, children: [] }; });
  categories.forEach(c => {
    if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id]);
    else roots.push(map[c.id]);
  });
  return roots;
}

function getCatName(cat, lang) {
  if (lang === 'sk' && cat.name_sk) return cat.name_sk;
  if (lang === 'ro' && cat.name_ro) return cat.name_ro;
  if (lang === 'en' && cat.name_en) return cat.name_en;
  if (lang === 'hu' && cat.name_hu) return cat.name_hu;
  return cat.name;
}

function CategoryTree({ nodes, selected, onSelect, level = 0, lang = 'cs' }) {
  const [expanded, setExpanded] = useState({});
  return (
    <div>
      {nodes.map(node => (
        <div key={node.id}>
          <div
            onClick={() => { onSelect(node.id === selected ? null : node.id); if (node.children.length) setExpanded(e => ({ ...e, [node.id]: !e[node.id] })); }}
            style={{
              padding: `7px 12px 7px ${16 + level * 16}px`,
              cursor: 'pointer',
              background: selected === node.id ? '#e8f4fc' : 'transparent',
              color: selected === node.id ? '#0e76bc' : '#374151',
              fontWeight: selected === node.id ? 700 : 500,
              fontSize: 14,
              borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 6,
              userSelect: 'none',
            }}
          >
            {node.children.length > 0 && (
              <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>{expanded[node.id] ? '▼' : '▶'}</span>
            )}
            {node.children.length === 0 && <span style={{ width: 14, flexShrink: 0 }} />}
            <span>{getCatName(node, lang)}</span>
          </div>
          {node.children.length > 0 && expanded[node.id] && (
            <CategoryTree nodes={node.children} selected={selected} onSelect={onSelect} level={level + 1} lang={lang} />
          )}
        </div>
      ))}
    </div>
  );
}

function ImageUpload({ value, onChange, token }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await fetch(`${API}/catalog/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.url) onChange(data.url);
    } catch (e) { alert('Chyba při nahrávání'); }
    setUploading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <button type="button" onClick={() => ref.current.click()} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {uploading ? 'Nahrávám...' : '📁 Vybrat obrázek'}
        </button>
        {value && <span style={{ fontSize: 12, color: '#6b7280' }}>✓ Obrázek nahrán</span>}
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
      {value && <img src={value.startsWith('http') ? value : `${window.location.origin}${value}`} alt="" style={{ maxHeight: 80, borderRadius: 6, border: '1px solid #e5e7eb' }} />}
    </div>
  );
}

function ProductForm({ product, categories, onSave, onCancel, token }) {
  const empty = { name: '', category_id: '', part_number: '', ean: '', description: '', image_url: '', is_active: 1, sort_order: 0 };
  const empty2 = { name: '', category_id: '', part_number: '', ean: '', description: '', description_cs: '', description_sk: '', description_ro: '', description_en: '', description_hu: '', image_url: '', is_active: 1, sort_order: 0, warranty_years: 0 };
  const [form, setForm] = useState(product ? { ...product } : empty2);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const flatCats = (nodes, level = 0) => nodes.flatMap(n => [{ ...n, level }, ...flatCats(n.children || [], level + 1)]);
  const tree = buildTree(categories);
  const flat = flatCats(tree);

  return (
    <div style={{ background: '#fff', border: '2px solid #0e76bc', borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0e76bc' }}>{product ? 'Upravit produkt' : 'Nový produkt'}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Název *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Kategorie</label>
          <select value={form.category_id || ''} onChange={e => set('category_id', e.target.value || null)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value="">-- Bez kategorie --</option>
            {flat.map(c => <option key={c.id} value={c.id}>{'\u00a0'.repeat(c.level * 3)}{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Stav</label>
          <select value={form.is_active} onChange={e => set('is_active', Number(e.target.value))} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value={1}>Aktuální</option>
            <option value={0}>Neaktuální</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>P/N (Part Number)</label>
          <input value={form.part_number} onChange={e => set('part_number', e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>EAN</label>
          <input value={form.ean} onChange={e => set('ean', e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Záruka (roky)</label>
          <input type="number" min="0" max="10" value={form.warranty_years || 0} onChange={e => set('warranty_years', parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Popis</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 6 }}>
            {[['cs','CS 🇨🇿'],['sk','SK 🇸🇰'],['ro','RO 🇷🇴'],['en','EN 🇬🇧'],['hu','HU 🇭🇺']].map(([l, label]) => (
              <div key={l}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>{label}</label>
                <textarea value={form[`description_${l}`]||''} onChange={e => set(`description_${l}`, e.target.value)} rows={3} style={{ width: '100%', padding: '7px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
            ))}
          </div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Obrázek</label>
          <ImageUpload value={form.image_url} onChange={v => set('image_url', v)} token={token} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button onClick={() => onSave(form)} disabled={!form.name} style={{ background: 'linear-gradient(135deg, #0e76bc, #0b5e96)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: !form.name ? 0.5 : 1 }}>Uložit</button>
        <button onClick={onCancel} style={{ background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Zrušit</button>
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const { user, apiFetch, t, lang } = useAuth();
  const isAdmin = user?.role === 'admin';
  const token = localStorage.getItem('token');

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [favorites, setFavorites] = useState([]);

  const loadFavorites = () => apiFetch('/catalog/favorites').then(setFavorites).catch(() => {});
  const toggleFavorite = async (e, productId) => {
    e.stopPropagation();
    const res = await apiFetch(`/catalog/products/${productId}/favorite`, { method: 'POST' });
    setFavorites(prev => res.favorited ? [...prev, productId] : prev.filter(id => id !== productId));
  };

  const toggleSelect = (p) => {
    setSelectedProducts(prev =>
      prev.find(x => x.id === p.id)
        ? prev.filter(x => x.id !== p.id)
        : [...prev, { id: p.id, product_id: p.id, product_name: p.name, product_sku: p.part_number || '', quantity: 1 }]
    );
  };

  const goToProject = () => {
    sessionStorage.setItem('prefillProducts', JSON.stringify(selectedProducts));
    sessionStorage.setItem('prefillType', 'project');
    // Navigate to projects page - use window event
    window.dispatchEvent(new CustomEvent('navigate', { detail: 'projects' }));
  };

  const goToLoan = () => {
    sessionStorage.setItem('prefillProducts', JSON.stringify(selectedProducts));
    sessionStorage.setItem('prefillType', 'loan');
    window.dispatchEvent(new CustomEvent('navigate', { detail: 'loans' }));
  };

  const loadCategories = () => apiFetch('/catalog/categories').then(setCategories);
  const loadProducts = () => {
    const params = new URLSearchParams();
    if (selectedCat) params.set('category_id', selectedCat);
    return apiFetch(`/catalog/products?${params}`).then(setProducts).finally(() => setLoading(false));
  };

  useEffect(() => { loadCategories(); loadFavorites(); }, []);
  useEffect(() => { setLoading(true); loadProducts(); }, [selectedCat]);

  const handleSaveProduct = async (form) => {
    if (editingProduct) await apiFetch(`/catalog/products/${editingProduct.id}`, { method: 'PUT', body: JSON.stringify(form) });
    else await apiFetch('/catalog/products', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); setEditingProduct(null); loadProducts();
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Opravdu smazat produkt?')) return;
    await apiFetch(`/catalog/products/${id}`, { method: 'DELETE' }); loadProducts();
  };

  const tree = buildTree(categories);

  // Get all category IDs in subtree
  const getSubtreeIds = (catId, cats) => {
    const result = [catId];
    const children = cats.filter(c => c.parent_id === catId);
    children.forEach(c => result.push(...getSubtreeIds(c.id, cats)));
    return result;
  };

  const filtered = products.filter(p => {
    if (selectedCat) {
      const ids = getSubtreeIds(selectedCat, categories);
      if (!ids.includes(p.category_id)) return false;
    }
    if (filterActive === 'active' && !p.is_active) return false;
    if (filterActive === 'inactive' && p.is_active) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !(p.part_number||'').toLowerCase().includes(q) && !(p.ean||'').toLowerCase().includes(q) && !(p.description||'').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const imageBase = window.location.origin.replace(':3000', ':3001');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📦 Katalog produktů</h1>
        {isAdmin && !showForm && !editingProduct && (
          <button onClick={() => setShowForm(true)} style={{ background: 'linear-gradient(135deg, #0e76bc, #0b5e96)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Přidat produkt
          </button>
        )}
      </div>
      <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: 14 }}>Přehled všech produktů Grandstream</p>

      {(showForm || editingProduct) && (
        <ProductForm
          product={editingProduct}
          categories={categories}
          onSave={handleSaveProduct}
          onCancel={() => { setShowForm(false); setEditingProduct(null); }}
          token={token}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
        {/* Left: category tree */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', alignSelf: 'start', position: 'sticky', top: 80 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', padding: '0 12px 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Kategorie</div>
          <div onClick={() => setSelectedCat(null)} style={{ padding: '7px 12px', cursor: 'pointer', background: !selectedCat ? '#e8f4fc' : 'transparent', color: !selectedCat ? '#0e76bc' : '#374151', fontWeight: !selectedCat ? 700 : 500, fontSize: 14, borderRadius: 6, userSelect: 'none' }}>
            Všechny produkty
          </div>
          <CategoryTree nodes={tree} selected={selectedCat} onSelect={setSelectedCat} lang={lang} />
        </div>

        {/* Right: products */}
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text" placeholder="Hledat podle názvu, P/N, EAN..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 14px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
            />
            <select value={filterActive} onChange={e => setFilterActive(e.target.value)} style={{ padding: '8px 14px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff', cursor: 'pointer' }}>
              <option value="all">Vše ({products.length})</option>
              <option value="active">Aktuální ({products.filter(p => p.is_active).length})</option>
              <option value="inactive">Neaktuální ({products.filter(p => !p.is_active).length})</option>
            </select>
            <span style={{ fontSize: 13, color: '#9ca3af' }}>{filtered.length} produktů</span>
          </div>

          {/* Selection action bar */}
          {selectedProducts.length > 0 && (
            <div style={{ background: '#fff', border: '2px solid #0e76bc', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0e76bc' }}>
                ✓ Vybráno {selectedProducts.length} {selectedProducts.length === 1 ? 'produkt' : selectedProducts.length < 5 ? 'produkty' : 'produktů'}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setSelectedProducts([])} style={{ background: 'none', border: '1px solid #e5e7eb', color: '#6b7280', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Zrušit výběr</button>
                <button onClick={goToLoan} style={{ background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🔄 Požádat o zápůjčku</button>
                <button onClick={goToProject} style={{ background: 'linear-gradient(135deg, #0e76bc, #0b5e96)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>📁 Požádat o projekt</button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Načítám...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px solid #f3f4f6' }}>
              {isAdmin ? 'Žádné produkty. Přidejte první produkt.' : 'Žádné produkty v této kategorii.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {filtered.map(p => {
                const imgSrc = p.image_url ? (p.image_url.startsWith('http') ? p.image_url : `${imageBase}${p.image_url}`) : null;
                return (
                  <div key={p.id} onClick={() => p.is_active && toggleSelect(p)} style={{ background: '#fff', borderRadius: 12, border: `1px solid ${selectedProducts.find(x => x.id === p.id) ? '#0e76bc' : '#f3f4f6'}`, boxShadow: selectedProducts.find(x => x.id === p.id) ? '0 0 0 2px #b3d9f2' : '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', opacity: p.is_active ? 1 : 0.6, display: 'flex', flexDirection: 'column', cursor: p.is_active ? 'pointer' : 'default', position: 'relative' }}>
                    {p.is_active && (
                      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${selectedProducts.find(x => x.id === p.id) ? '#0e76bc' : '#d1d5db'}`, background: selectedProducts.find(x => x.id === p.id) ? '#0e76bc' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {selectedProducts.find(x => x.id === p.id) && <span style={{ color: '#fff', fontSize: 14, lineHeight: 1 }}>✓</span>}
                        </div>
                      </div>
                    )}
                    {imgSrc ? (
                      <div style={{ height: 160, overflow: 'hidden', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={imgSrc} alt={p.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', padding: 8 }} />
                      </div>
                    ) : (
                      <div style={{ height: 120, background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d1d5db', fontSize: 40 }}>📦</div>
                    )}
                    <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>{p.name}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <button onClick={(e) => toggleFavorite(e, p.id)} title={favorites.includes(p.id) ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 2px', lineHeight: 1, filter: favorites.includes(p.id) ? 'none' : 'grayscale(1) opacity(0.3)' }}>
                            👍
                          </button>
                          <span style={{ background: p.is_active ? '#f0fdf4' : '#f9fafb', color: p.is_active ? '#166534' : '#9ca3af', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>
                            {p.is_active ? 'Aktuální' : 'Neaktuální'}
                          </span>
                        </div>
                      </div>
                      {p.part_number && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>P/N: <span style={{ fontFamily: 'monospace', color: '#374151' }}>{p.part_number}</span></div>}
                      {p.ean && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>EAN: <span style={{ fontFamily: 'monospace', color: '#374151' }}>{p.ean}</span></div>}
            {p.warranty_years > 0 && <div style={{ fontSize: 12, color: '#166534', marginBottom: 6, fontWeight: 600 }}>🛡️ Záruka: {p.warranty_years} {p.warranty_years === 1 ? 'rok' : p.warranty_years < 5 ? 'roky' : 'let'}</div>}
                      {(() => { const desc = p[`description_${lang}`] || p.description_en || p.description_cs || p.description; return desc ? <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5, flex: 1 }}>{desc}</p> : null; })()}
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button onClick={() => { setEditingProduct(p); setShowForm(false); window.scrollTo(0,0); }} style={{ flex: 1, background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Upravit</button>
                          <button onClick={() => handleDeleteProduct(p.id)} style={{ background: '#e8f4fc', border: '1px solid #b3d9f2', color: '#dc2626', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Smazat</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
